import { useEffect, useState } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { Form, Input, Button, Alert, Result, Spin } from 'antd'
import { LockOutlined } from '@ant-design/icons'
import axiosInstance from '@/lib/axios'
import { API_ENDPOINTS } from '@/config/api'

const SetPasswordPage = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const token = searchParams.get('token') ?? ''

  const [validating, setValidating] = useState(true)
  const [tokenValid, setTokenValid] = useState(false)
  const [userEmail, setUserEmail] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [done, setDone] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!token) {
      setValidating(false)
      return
    }
    axiosInstance
      .get(`${API_ENDPOINTS.VALIDATE_TOKEN}?token=${encodeURIComponent(token)}`)
      .then((res) => {
        setTokenValid(res.data.valid)
        setUserEmail(res.data.email ?? null)
      })
      .catch(() => setTokenValid(false))
      .finally(() => setValidating(false))
  }, [token])

  const onFinish = async (values: { password: string }) => {
    setError(null)
    setLoading(true)
    try {
      await axiosInstance.post(API_ENDPOINTS.SET_PASSWORD, {
        token,
        password: values.password,
      })
      setDone(true)
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Errore durante il salvataggio. Riprova.')
    } finally {
      setLoading(false)
    }
  }

  if (validating) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Spin size="large" tip="Verifica in corso..." />
      </div>
    )
  }

  if (done) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--color-bg-container)', padding: 24 }}>
        <Result
          status="success"
          title="Password impostata"
          subTitle="Il tuo account è attivo. Puoi ora accedere con email e password."
          extra={
            <Button type="primary" onClick={() => navigate('/')} style={{ height: 44, fontWeight: 600 }}>
              Vai al login
            </Button>
          }
        />
      </div>
    )
  }

  if (!tokenValid) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--color-bg-container)', padding: 24 }}>
        <Result
          status="warning"
          title="Link non valido o scaduto"
          subTitle="Il link che hai usato è scaduto o è già stato utilizzato. Richiedi un nuovo link."
          extra={[
            <Button key="forgot" type="primary" onClick={() => navigate('/forgot-password')}>
              Richiedi nuovo link
            </Button>,
            <Button key="login" onClick={() => navigate('/')}>
              Torna al login
            </Button>,
          ]}
        />
      </div>
    )
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--color-bg-container)', padding: 24 }}>
      <div style={{ width: '100%', maxWidth: 400 }}>
        <div style={{ marginBottom: 32 }}>
          <h2 style={{ fontSize: 26, fontWeight: 700, color: 'var(--color-text)', margin: '0 0 8px', letterSpacing: '-0.01em' }}>
            Imposta la tua password
          </h2>
          {userEmail && (
            <p style={{ fontSize: 14, color: 'var(--color-text-secondary)', margin: 0 }}>
              Account: <strong>{userEmail}</strong>
            </p>
          )}
        </div>

        {error && (
          <Alert type="error" message={error} showIcon style={{ marginBottom: 20 }} />
        )}

        <Form layout="vertical" onFinish={onFinish} size="large">
          <Form.Item
            name="password"
            label="Nuova password"
            rules={[
              { required: true, message: 'Inserisci la password' },
              { min: 8, message: 'La password deve essere di almeno 8 caratteri' },
            ]}
          >
            <Input.Password
              prefix={<LockOutlined style={{ color: '#bfbfbf' }} />}
              placeholder="Minimo 8 caratteri"
              autoComplete="new-password"
            />
          </Form.Item>

          <Form.Item
            name="confirm"
            label="Conferma password"
            dependencies={['password']}
            rules={[
              { required: true, message: 'Conferma la password' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) {
                    return Promise.resolve()
                  }
                  return Promise.reject(new Error('Le password non corrispondono'))
                },
              }),
            ]}
          >
            <Input.Password
              prefix={<LockOutlined style={{ color: '#bfbfbf' }} />}
              placeholder="Ripeti la password"
              autoComplete="new-password"
            />
          </Form.Item>

          <Button
            type="primary"
            htmlType="submit"
            loading={loading}
            block
            style={{ height: 48, fontWeight: 600, marginTop: 8 }}
          >
            Salva password
          </Button>
        </Form>
      </div>
    </div>
  )
}

export default SetPasswordPage
